"use client";

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ClipboardEvent,
} from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "~/components/ui/dialog";
import { X } from "lucide-react";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Button } from "~/components/ui/button";
import { useUserInfo } from "~/components/user-info-provider";
import { authClient } from "~/utils/auth-client";
import { UserRoleEnum } from "~/lib/objects";
import { useAdminAccess } from "~/features/adminAccess/useAdminAccess";
import type { PgnPlayerNamesTarget } from "./contracts";
import { replacePgnPlayerNameLineBreaks } from "./lineBreaks";

function isSuccess(
  value: unknown,
): value is PgnPlayerNamesTarget & { success: true } {
  if (typeof value !== "object" || value === null) return false;
  const result = value as Record<string, unknown>;
  return (
    result.success === true &&
    typeof result.userId === "number" &&
    Number.isSafeInteger(result.userId) &&
    result.userId > 0 &&
    typeof result.login === "string" &&
    result.login.trim() !== "" &&
    Array.isArray(result.pgnPlayerNames) &&
    result.pgnPlayerNames.every((name: unknown) => typeof name === "string")
  );
}

function isFailure(value: unknown): boolean {
  if (typeof value !== "object" || value === null) return false;
  const result = value as Record<string, unknown>;
  return result.success === false && typeof result.message === "string";
}

const unknownSave =
  "Не удалось подтвердить сохранение. Загрузите настройку повторно, чтобы проверить результат.";

export interface UserPgnPlayerNamesEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRestoreFocus?: () => void;
}

export function UserPgnPlayerNamesEditor({
  open,
  onOpenChange,
  onRestoreFocus,
}: UserPgnPlayerNamesEditorProps) {
  const id = useId();
  const openRef = useRef(open);
  openRef.current = open;
  const {
    refetchUserInfo,
    userInfo,
    resolutionStatus,
    resolutionLifetime,
  } = useUserInfo();
  const adminAccess = useAdminAccess();
  const sessionState = authClient.useSession();
  const sessionPending =
    sessionState.isPending ||
    ("isRefetching" in sessionState && sessionState.isRefetching === true);
  const accessPending = sessionPending || resolutionStatus === "pending";
  const canEdit =
    adminAccess.canRequest &&
    !sessionPending &&
    !sessionState.error &&
    resolutionStatus === "resolved" &&
    userInfo?.role === UserRoleEnum.ADMIN;
  const accessError = !!sessionState.error || resolutionStatus === "error";
  const [retryPending, setRetryPendingState] = useState(false);
  const activeRetry = useRef(false);
  const setRetryPending = useCallback((next: boolean) => {
    activeRetry.current = next;
    setRetryPendingState(next);
  }, []);
  useEffect(() => {
    if (accessPending || canEdit) setRetryPending(false);
  }, [accessPending, canEdit, setRetryPending]);
  function retryAccess() {
    if (activeRetry.current || accessPending) return;
    setRetryPending(true);
    if (sessionState.error) void sessionState.refetch();
    else if (sessionState.data) refetchUserInfo();
  }
  const [login, setLogin] = useState("");
  const [target, setTarget] = useState<PgnPlayerNamesTarget>();
  const [draft, setDraft] = useState("");
  const namesInput = useRef<HTMLInputElement>(null);
  const pastedCaret = useRef<number>();
  useLayoutEffect(() => {
    if (pastedCaret.current !== undefined) {
      namesInput.current?.setSelectionRange(
        pastedCaret.current,
        pastedCaret.current,
      );
      pastedCaret.current = undefined;
    }
  }, [draft]);
  function pasteNames(event: ClipboardEvent<HTMLInputElement>) {
    if (!canEdit || !target || operation || referenceOnly) return;
    event.preventDefault();
    const input = event.currentTarget;
    const inserted = replacePgnPlayerNameLineBreaks(
      event.clipboardData.getData("text/plain"),
    );
    const start = input.selectionStart ?? draft.length;
    const end = input.selectionEnd ?? start;
    const next = draft.slice(0, start) + inserted + draft.slice(end);
    const caret = start + inserted.length;
    if (next === draft) input.setSelectionRange(caret, caret);
    else {
      pastedCaret.current = caret;
      setDraft(next);
    }
  }
  const [operation, setOperationState] = useState<"load" | "save">();
  const activeOperation = useRef<typeof operation>();
  const setOperation = useCallback((next: typeof operation) => {
    activeOperation.current = next;
    setOperationState(next);
  }, []);
  const [error, setError] = useState("");
  const [referenceOnly, setReferenceOnly] = useState(false);
  const requestId = useRef(0);
  useEffect(
    () => () => {
      requestId.current++;
    },
    [],
  );
  const confirmedSessionKey =
    !sessionPending && !sessionState.error
      ? sessionState.data
        ? JSON.stringify([
            sessionState.data.session.id,
            sessionState.data.user.id,
          ])
        : null
      : undefined;
  const identity = useRef({
    lifetime: resolutionLifetime,
    sessionKey: confirmedSessionKey,
    adminLifetime: adminAccess.lifetime,
  });
  const latestIdentity = useRef(identity.current);
  latestIdentity.current = {
    lifetime: resolutionLifetime,
    adminLifetime: adminAccess.lifetime,
    sessionKey:
      confirmedSessionKey === undefined
        ? latestIdentity.current.sessionKey
        : confirmedSessionKey,
  };
  function isCurrentRequest(
    request: number,
    authorization: typeof latestIdentity.current,
  ) {
    return (
      openRef.current &&
      request === requestId.current &&
      authorization.lifetime === latestIdentity.current.lifetime &&
      authorization.sessionKey === latestIdentity.current.sessionKey &&
      adminAccess.isCurrent(authorization.adminLifetime)
    );
  }
  function changeLogin(value: string) {
    reset();
    setLogin(value);
  }
  const reset = useCallback(() => {
    requestId.current++;
    setLogin("");
    setTarget(undefined);
    setDraft("");
    setOperation(undefined);
    setError("");
    setReferenceOnly(false);
    setRetryPending(false);
  }, [setOperation, setRetryPending]);
  const close = useCallback(() => {
    reset();
    onOpenChange(false);
  }, [reset, onOpenChange]);
  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);
  const confirmedDeparture =
    sessionState.error?.status === 401 ||
    sessionState.error?.status === 403 ||
    (!sessionPending && !sessionState.error && !sessionState.data) ||
    resolutionStatus === "anonymous" ||
    (resolutionStatus === "resolved" &&
      !!userInfo &&
      userInfo.role !== UserRoleEnum.ADMIN);
  useEffect(() => {
    const changed =
      identity.current.lifetime !== resolutionLifetime ||
      identity.current.adminLifetime !== adminAccess.lifetime ||
      (confirmedSessionKey !== undefined &&
        identity.current.sessionKey !== confirmedSessionKey);
    identity.current = {
      lifetime: resolutionLifetime,
      adminLifetime: adminAccess.lifetime,
      sessionKey:
        confirmedSessionKey === undefined
          ? identity.current.sessionKey
          : confirmedSessionKey,
    };
    if (open && (confirmedDeparture || changed)) {
      close();
    }
  }, [
    open,
    confirmedDeparture,
    confirmedSessionKey,
    resolutionLifetime,
    adminAccess.lifetime,
    close,
  ]);
  function authorizationLost(status: number, adminLifetime: object) {
    if (status !== 401 && status !== 403) return false;
    close();
    adminAccess.reportDenial(adminLifetime, status);
    return true;
  }
  async function load() {
    if (!openRef.current || !canEdit || activeOperation.current) return;
    if (!login.trim()) {
      setError("Укажите логин пользователя.");
      return;
    }
    const request = ++requestId.current;
    const authorization = latestIdentity.current;
    const selectedLogin = login.trim();
    setOperation("load");
    setTarget(undefined);
    setError("");
    try {
      const response = await fetch(
        `/api/admin/users/pgn-player-names?login=${encodeURIComponent(selectedLogin)}`,
        { cache: "no-store" },
      );
      if (!isCurrentRequest(request, authorization)) return;
      if (authorizationLost(response.status, authorization.adminLifetime)) return;
      if (!response.ok) {
        setError(
          response.status === 404
            ? "Пользователь не найден."
            : "Не удалось загрузить имена игрока.",
        );
        return;
      }
      const result: unknown = await response.json();
      if (!isCurrentRequest(request, authorization)) return;
      if (!isSuccess(result) || result.login !== selectedLogin) {
        setError("Не удалось загрузить имена игрока.");
        return;
      }
      setTarget(result);
      setLogin(result.login);
      setDraft(result.pgnPlayerNames.join("; "));
      setReferenceOnly(false);
    } catch {
      if (isCurrentRequest(request, authorization))
        setError("Не удалось загрузить имена игрока.");
    } finally {
      if (isCurrentRequest(request, authorization)) setOperation(undefined);
    }
  }
  async function save() {
    if (!openRef.current || !canEdit || !target || activeOperation.current)
      return;
    const request = ++requestId.current;
    const authorization = latestIdentity.current;
    setOperation("save");
    setError("");
    try {
      const response = await fetch("/api/admin/users/pgn-player-names", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: target.userId,
          login: target.login,
          pgnPlayerNames: draft,
        }),
      });
      if (!isCurrentRequest(request, authorization)) return;
      if (authorizationLost(response.status, authorization.adminLifetime)) return;
      if (!response.ok) {
        if (response.status === 404 || response.status === 409) {
          setTarget(undefined);
          setReferenceOnly(true);
        }
        const failure: unknown = await response.json();
        if (!isCurrentRequest(request, authorization)) return;
        if (!isFailure(failure)) {
          setError(unknownSave);
          return;
        }
        setError(
          response.status === 404
            ? "Пользователь не найден."
            : response.status === 409
              ? "Логин пользователя изменился. Загрузите пользователя повторно."
              : response.status === 400
                ? "Некорректные данные настройки."
                : "Не удалось сохранить имена игрока.",
        );
        return;
      }
      const result: unknown = await response.json();
      if (!isCurrentRequest(request, authorization)) return;
      if (
        !isSuccess(result) ||
        result.userId !== target.userId ||
        result.login !== target.login
      ) {
        setError(unknownSave);
        return;
      }
      close();
    } catch {
      if (isCurrentRequest(request, authorization)) {
        setError(unknownSave);
      }
    } finally {
      if (isCurrentRequest(request, authorization)) setOperation(undefined);
    }
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent
        showCloseButton={false}
        onCloseAutoFocus={(event) => {
          if (onRestoreFocus) {
            event.preventDefault();
            if (!openRef.current) onRestoreFocus();
          }
        }}
      >
        <DialogClose asChild>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-2 top-2"
            aria-label="Закрыть окно"
          >
            <X className="h-4 w-4" />
          </Button>
        </DialogClose>
        <DialogHeader>
          <DialogTitle>Имена игрока в PGN</DialogTitle>
          <DialogDescription>
            Укажите имена пользователя из тегов White и Black в PGN. Разделяйте
            имена точкой с запятой. Запятые внутри имени сохраняются. Переносы
            строк заменяются пробелами.
          </DialogDescription>
        </DialogHeader>
        <form
          aria-label="Загрузка пользователя"
          onSubmit={(event) => {
            event.preventDefault();
            void load();
          }}
          className="grid gap-4"
        >
          <Label htmlFor={`${id}-login`}>Логин пользователя</Label>
          <Input
            id={`${id}-login`}
            value={login}
            disabled={!canEdit || operation === "save"}
            onChange={(event) => changeLogin(event.target.value)}
          />
          <Button
            type="submit"
            disabled={!canEdit || !login.trim() || !!operation}
          >
            {operation === "load" ? "Загрузка..." : "Загрузить"}
          </Button>
        </form>
        <Label htmlFor={`${id}-names`}>Имена через точку с запятой</Label>
        <Input
          ref={namesInput}
          id={`${id}-names`}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onPaste={pasteNames}
          readOnly={referenceOnly}
          disabled={!canEdit || (!target && !referenceOnly) || !!operation}
          placeholder="Rausis, Igors; Егорушкин; redfox"
        />
        {accessPending && <p role="status">Проверка доступа...</p>}
        {accessError && (
          <>
            <p role="alert">
              Не удалось проверить права доступа. Повторите попытку.
            </p>
            <Button
              disabled={accessPending || retryPending}
              onClick={retryAccess}
            >
              Повторить проверку
            </Button>
          </>
        )}
        {error && <p role="alert">{error}</p>}
        {operation === "save" && (
          <p role="status">Закрытие окна не отменяет сохранение.</p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            {operation === "save" ? "Закрыть" : "Отмена"}
          </Button>
          <Button disabled={!canEdit || !target || !!operation} onClick={save}>
            {operation === "save" ? "Сохранение..." : "Сохранить"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
